import InvoiceStateBadge from '@/components/invoices/InvoiceStateBadge'
import MissingEmailModal from '@/components/recaps/MissingEmailModal'
import MonthNavigation from '@/components/recaps/MonthNavigation'
import { useRecapActions } from '@/hooks/useRecapActions'
import { invoicingSkipReasonLabels } from '@/constants'
import { Contract, Invoice, RecapRow, SharedProps, VatBreakdownGroup } from '@/types'
import { formatCurrency, formatVatRate } from '@/utils/format'
import { Head, Link, router, usePage } from '@inertiajs/react'
import { Anchor, Button, Group, Paper, Stack, Table, Text, Title, UnstyledButton } from '@mantine/core'
import { IconChevronLeft, IconDownload, IconFileInvoice, IconSend } from '@tabler/icons-react'
import dayjs from 'dayjs'

interface RecapsShowPageProps extends SharedProps {
  contract: Contract
  month: string
  rows: RecapRow[]
  set_aside_rows: RecapRow[]
  total: number
  vat_breakdown: VatBreakdownGroup[] | null
  total_including_tax: number | null
  basis_label: string | null
  invoices: Invoice[]
  billable_count: number
  e_invoicing_connected: boolean
}

export default function RecapsShow({
  contract,
  month,
  rows,
  set_aside_rows,
  total,
  vat_breakdown,
  total_including_tax,
  basis_label,
  invoices,
  billable_count,
  e_invoicing_connected,
}: RecapsShowPageProps) {
  const { current_organization } = usePage<SharedProps>().props

  const basePath = `/organizations/${current_organization?.slug}/recaps`

  const { sendingContractId, missingEmailContract, closeMissingEmail, downloadPdf, sendEmail } = useRecapActions(
    current_organization?.slug,
    month,
  )

  const handleBackToList = () => router.get(basePath, { month }, { preserveState: true })

  const handleMonthChange = (value: string) =>
    router.get(`${basePath}/${contract.id}`, { month: value }, { preserveState: true })

  const handleDownloadPdf = () => downloadPdf(contract)

  const handleSendEmail = () => sendEmail(contract)

  const handleCreateInvoice = () =>
    router.get(`/organizations/${current_organization?.slug}/invoices/new`, { contract_id: contract.id, month })

  const formatDate = (date: string | null) => (date ? dayjs(date).format('DD/MM/YYYY') : '')

  const isEmpty = rows.length === 0 && set_aside_rows.length === 0
  const taxes = (vat_breakdown ?? []).filter((group) => Number(group.rate) > 0)
  const isFranchise = current_organization?.vat_regime === 'franchise'

  return (
    <>
      <Head title={`Récapitulatif Sociétés - ${current_organization?.name}`} />

      <Stack gap="md">
        <UnstyledButton onClick={handleBackToList} w="fit-content">
          <Group gap={4} c="dimmed">
            <IconChevronLeft size={18} />
            <Text size="sm">Sociétés</Text>
          </Group>
        </UnstyledButton>

        <Title order={3}>
          {`Récapitulatif : ${contract.customer_name} — ${dayjs(`${month}-01`).format('MMMM YYYY')}`}
        </Title>
        {basis_label && (
          <Text size="sm" c="dimmed" mt={-8}>
            {basis_label}
          </Text>
        )}

        <Group gap="sm" justify="space-between">
          <MonthNavigation month={month} onChange={handleMonthChange} />
          <Group gap="sm">
            {e_invoicing_connected && billable_count > 0 && (
              <Button color="teal" leftSection={<IconFileInvoice size={16} />} onClick={handleCreateInvoice}>
                Créer la facture
              </Button>
            )}
            <Button
              variant="light"
              leftSection={<IconDownload size={16} />}
              onClick={handleDownloadPdf}
              disabled={isEmpty}
            >
              Télécharger PDF
            </Button>
            <Button
              leftSection={<IconSend size={16} />}
              onClick={handleSendEmail}
              loading={sendingContractId === contract.id}
              disabled={isEmpty}
            >
              Envoyer par email
            </Button>
          </Group>
        </Group>

        {invoices.length > 0 && (
          <Group gap="sm">
            <Text size="sm" c="dimmed">
              Factures du mois :
            </Text>
            {invoices.map((invoice) => (
              <Group key={invoice.id} gap={6}>
                <Anchor component={Link} href={`/organizations/${current_organization?.slug}/invoices/${invoice.id}`} size="sm">
                  {invoice.number}
                </Anchor>
                <InvoiceStateBadge state={invoice.state} size="sm" />
              </Group>
            ))}
            {billable_count > 0 && (
              <Text size="sm" c="orange.8">
                {`${billable_count} prestation${billable_count > 1 ? 's' : ''} pas encore facturée${billable_count > 1 ? 's' : ''}`}
              </Text>
            )}
          </Group>
        )}

        <Paper withBorder radius="md" p={{ base: 0, sm: 'md' }}>
          <Table.ScrollContainer minWidth={800}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Réception</Table.Th>
                  <Table.Th>Article</Table.Th>
                  <Table.Th>Service</Table.Th>
                  <Table.Th>Référence</Table.Th>
                  <Table.Th>Livraison</Table.Th>
                  <Table.Th>Instructions</Table.Th>
                  <Table.Th w={110}>Montant HT</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={7}>
                      <Text c="dimmed" ta="center" py="md">
                        Aucune commande pour cette période
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
                {rows.map((row, index) => (
                  <Table.Tr key={index}>
                    <Table.Td>{formatDate(row.reception_date)}</Table.Td>
                    <Table.Td>{row.article_name}</Table.Td>
                    <Table.Td>{row.service_name}</Table.Td>
                    <Table.Td>{row.reference}</Table.Td>
                    <Table.Td>{formatDate(row.delivery_date)}</Table.Td>
                    <Table.Td>{row.instructions}</Table.Td>
                    <Table.Td>{formatCurrency(row.price)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
              {rows.length > 0 && (
                <Table.Tfoot>
                  <Table.Tr style={{ borderTop: '2px solid var(--mantine-color-dark-4)' }}>
                    <Table.Td colSpan={6} fw={700} ta="right">
                      Total HT
                    </Table.Td>
                    <Table.Td fw={700}>{formatCurrency(total)}</Table.Td>
                  </Table.Tr>
                  {taxes.map((group) => (
                    <Table.Tr key={group.rate}>
                      <Table.Td colSpan={6} ta="right">
                        {`TVA ${formatVatRate(group.rate)}`}
                      </Table.Td>
                      <Table.Td>{formatCurrency(group.tax)}</Table.Td>
                    </Table.Tr>
                  ))}
                  {total_including_tax !== null && (
                    <Table.Tr>
                      <Table.Td colSpan={6} fw={700} ta="right">
                        {isFranchise ? 'Total TTC — TVA non applicable, art. 293 B du CGI' : 'Total TTC'}
                      </Table.Td>
                      <Table.Td fw={700}>{formatCurrency(total_including_tax)}</Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tfoot>
              )}
            </Table>
          </Table.ScrollContainer>
        </Paper>

        {set_aside_rows.length > 0 && (
          <Stack gap="xs">
            <Title order={5}>Prestations non facturées</Title>
            <Paper withBorder radius="md" p={{ base: 0, sm: 'md' }}>
              <Table.ScrollContainer minWidth={800}>
                <Table c="dimmed">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Réception</Table.Th>
                      <Table.Th>Article</Table.Th>
                      <Table.Th>Service</Table.Th>
                      <Table.Th>Motif</Table.Th>
                      <Table.Th w={110}>Montant HT</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {set_aside_rows.map((row, index) => (
                      <Table.Tr key={index}>
                        <Table.Td>{formatDate(row.reception_date)}</Table.Td>
                        <Table.Td>{row.article_name}</Table.Td>
                        <Table.Td>{row.service_name}</Table.Td>
                        <Table.Td>{row.set_aside_reason && invoicingSkipReasonLabels[row.set_aside_reason]}</Table.Td>
                        <Table.Td>{formatCurrency(row.price)}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </Paper>
          </Stack>
        )}
      </Stack>

      <MissingEmailModal contract={missingEmailContract} onClose={closeMissingEmail} />
    </>
  )
}
